import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { ArrowRight, Loader2 } from 'lucide-react'
import { ApiError, inquiriesApi } from '@/utils/api'

type PartnerFormState = {
    name: string
    organization: string
    email: string
    phone: string
}

const EMPTY_STATE: PartnerFormState = {
    name: '',
    organization: '',
    email: '',
    phone: '',
}

type PartnerModalProps = {
    trigger: React.ReactNode
}

export default function PartnerModal({ trigger }: PartnerModalProps) {
    const [open, setOpen] = useState(false)
    const [form, setForm] = useState<PartnerFormState>(EMPTY_STATE)
    const [submitting, setSubmitting] = useState(false)
    const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

    const handleChange = (field: keyof PartnerFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }))
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (submitting) return
        setSubmitting(true)
        setStatus(null)

        try {
            await inquiriesApi.partner({
                name: form.name,
                organization: form.organization,
                email: form.email,
                ...(form.phone ? { phone: form.phone } : {}),
            })
            setStatus({
                type: 'success',
                message: 'Thanks! Your inquiry has been sent — our team will reach out shortly.',
            })
            setForm(EMPTY_STATE)
            setTimeout(() => {
                setOpen(false)
                setStatus(null)
            }, 2000)
        } catch (error) {
            const message =
                error instanceof ApiError
                    ? error.message
                    : 'Something went wrong sending your inquiry. Please try again.'
            setStatus({ type: 'error', message })
        } finally {
            setSubmitting(false)
        }
    }

    const handleOpenChange = (next: boolean) => {
        if (submitting) return
        setOpen(next)
        if (!next) setStatus(null)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="sm:max-w-[480px] rounded-[24px] p-8">
                <DialogHeader className="space-y-2 text-start">
                    <DialogTitle className="text-2xl font-extrabold text-[#1c1c1f]">
                        Let&apos;s Collaborate
                    </DialogTitle>
                    <DialogDescription className="text-[#4e4d4d] text-sm leading-relaxed">
                        Share a few details and our team will reach out about bringing IRIS into your organization or
                        religious institution.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                    <div className="space-y-2">
                        <label htmlFor="partner-name" className="block text-sm font-medium text-gray-700">
                            Name
                        </label>
                        <input
                            id="partner-name"
                            type="text"
                            required
                            value={form.name}
                            onChange={handleChange('name')}
                            placeholder="Jane Doe"
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f60]"
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="partner-org" className="block text-sm font-medium text-gray-700">
                            Organization / Religious Institution Name
                        </label>
                        <input
                            id="partner-org"
                            type="text"
                            required
                            value={form.organization}
                            onChange={handleChange('organization')}
                            placeholder="Citizens Gavel"
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f60]"
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="partner-email" className="block text-sm font-medium text-gray-700">
                            Email
                        </label>
                        <input
                            id="partner-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={handleChange('email')}
                            placeholder="you@example.com"
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f60]"
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="partner-phone" className="block text-sm font-medium text-gray-700">
                            Contact Phone Number
                        </label>
                        <input
                            id="partner-phone"
                            type="tel"
                            required
                            value={form.phone}
                            onChange={handleChange('phone')}
                            placeholder="+234 800 000 0000"
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f60]"
                        />
                    </div>

                    {status && (
                        <p
                            className={`text-sm ${
                                status.type === 'success' ? 'text-green-600' : 'text-red-600'
                            }`}
                            role="status"
                        >
                            {status.message}
                        </p>
                    )}

                    <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#f60] hover:bg-[#e55a00] text-[#1c1c1f] rounded-[75px] px-[20px] py-[15px] text-[16px] font-semibold transition-all group w-full mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            <>
                                Send Inquiry
                                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </>
                        )}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    )
}
